import jq from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import autoFillForms from '@shared/auto-fill-forms';
import { T_ } from '@shared/translation';
import LoginModel from './models/login.js';
import SignupModel from './models/signup.js';
import CaptchaModel from './models/captcha.js';
import RecoverPasswordModel from './models/recoverpassword.js';
import FBLoginModel from './models/fblogin.js';
import LoginView from './views/login.js';
import SignupView from './views/signup.js';
import CaptchaView from './views/captcha.js';
import RecoverPasswordView from './views/recoverpassword.js';
import FBLoginView from './views/fblogin.js';

/** *
 * The LoginSignup module enables you to integrates an highly
 * customisable login and/or signup funnel.
 *
 * @param {object} Global options passed as a javascript object
 * Key values are:
 * - el {DOMElement} The DOM Element on which the login/signup funnel
 *   should be attached
 * - loginClbk {function} The JavaScript function to be called upon
 *   successful login
 * - employerSignupClbk {function} The JavaScript function to be called
 *   upon successul signup of an 'Employer'
 * - workerSignupClbk {function} The JavaScript function to be called
 *   upon successul signup of a 'Worker'
 */

export default Backbone.Router.extend({
  initialize(options) {
    // Global options
    // We jq it again so that we can pass DOM element
    // in other modules without jq imported
    this.el = jq(options.el);
    this.loginClbk = options.loginClbk;
    this.employerSignupClbk = options.employerSignupClbk;
    this.workerSignupClbk = options.workerSignupClbk;
    this.closeModalClbk = options.closeModalClbk;
    this.isLocalProject = options.isLocalProject;
    this.projectId = options.projectId;
    this.signupSkipCaptchaHash = this.getUrlParameter('secured_connection');
    this.isModalUpsell = options.isModalUpsell;
    this.upsellTemplate = options.upsellTemplate;
    // Sub-views events bindings
    this.vent = _.extend(options.vent || {}, Backbone.Events);
    this.backdrop = true;
    this.vent.bind('show:signup', this.showSignup, this);
    this.vent.bind('show:login', this.showLogin, this);
    this.vent.bind('show:captcha', this.showCaptcha, this);
    this.vent.bind('show:recoverpassword', this.showRecoverPassword, this);
    this.vent.bind('show:fblogin', this.showFBLogin, this);
    this.vent.bind('signup:success', this.onSignupSuccess, this);
    this.vent.bind('login:success', this.onLoginSuccess, this);
    this.vent.bind('login:redirection', this.onLoginRedirection, this);
    this.vent.bind('fblogin:loginsuccess', this.onLoginSuccess, this);
    this.vent.bind('fblogin:signupsuccess', this.onSignupSuccess, this);
    this.vent.bind('fblogin:redirection', this.onLoginRedirection, this);
    this.vent.bind('close', this.onClose, this);
    this.isBackdropSet = false; // flag to remember the state of the backdrop
  },
  getUrlParameter(param) {
    const pageURL = decodeURIComponent(window.location.search.substring(1));
    const urlVariables = pageURL.split('&');
    let parameterName;
    let i;
    for (i = 0; i < urlVariables.length; i++) {
      parameterName = urlVariables[i].split('=');

      if (parameterName[0] === param) {
        return parameterName[1];
      }
    }
  },

  /**
   * Show the signup funnel
   *
   * @param {object} Signup form specific options passed as a
   * JavaScript object.
   * Accepted keys are:
   */
  showSignup(options) {
    let signupModel;
    const signupSkipCaptchaHash =
      typeof this.getUrlParameter('secured_connection') !== 'undefined'
        ? this.getUrlParameter('secured_connection')
        : false;
    if (options && options.signupModel) {
      signupModel = options.signupModel;
    } else if (options) {
      options.signupSkipCaptchaHash = signupSkipCaptchaHash;

      let lookingFor = '';
      if (typeof options.isEmployer !== 'undefined') {
        lookingFor = options.isEmployer ? 'Employer' : 'Worker';
      }

      const config = {
        email: options.emailAddress,
        looking_for: lookingFor,
        signup_skip_captcha: options.signupSkipCaptchaHash,
        project_collaboration_invitation_token:
          options.project_collaboration_invitation_token,
      };
      signupModel = new SignupModel(config);
    } else {
      signupModel = new SignupModel();
    }

    // Options to allow prefill on modal open via JS
    if (options && options.prefill) {
      signupModel.set(options.prefill);
    }

    const isLogin = options && !!options.isLoginSignup;
    // LJ-375
    let switchToAlternateSignup = false;
    if (
      _.isObject(options) &&
      options.hasOwnProperty('switchToAlternateSignup') &&
      options.switchToAlternateSignup === true
    ) {
      switchToAlternateSignup = true;
      options.hideConfirmPassword = true;
    }
    const signupView = new SignupView({
      isModalUpsell: this.isModalUpsell,
      upsellTemplate: this.upsellTemplate,
      model: signupModel,
      projectId: this.projectId,
      vent: this.vent,
      error: options ? options.error : '',
      coupon: options && !!options.coupon,
      couponHiddenDefault: options && !!options.couponHiddenDefault,
      loginTitle: options ? options.loginTitle : '',
      hideFacebook: options && !!options.hideFacebook,
      hideLookingFor: options && !!options.hideLookingFor,
      isLoginSignup: isLogin,
      isFreemarket: options && !!options.isFreemarket,
      signupSkipCaptchaHash,
      // LPO-910
      hideConfirmPassword:
        _.isObject(options) && options.hasOwnProperty('hideConfirmPassword')
          ? options.hideConfirmPassword
          : false,
      // LJ-375
      switchToAlternateSignup,
      saveFormFields: options && !!options.saveFormFields,
      // CLT-15, CLT-14, projectCollaborators
      isInvitedCollaborator:
        options && options.project_collaboration_invitation_token,
    });

    this.switchView(signupView, isLogin);
    if (typeof options !== 'undefined' && options.saveFormFields) {
      const inputFields = ['email', 'username', 'looking_for'];
      autoFillForms.init('signup_page_save_fields', inputFields);
      autoFillForms.fillFormData();
    }
  },

  /**
   * Show the login funnel
   *
   * @param {object} Login funnel specific options passed as a
   * JavaScript object.
   * Accepted keys are:
   * - title {string} Custom title. Default: "Login to
   *   Freelancer.com".
   * - welcomeMsg {string} Custom welcome message. Default: empty
   * - keep {boolean} Prevent users from existing the funnel.
   *   Default: false.
   */
  showLogin(options) {
    const o = _.extend({}, options);
    const loginModel = new LoginModel({
      goto_url: o.gotoUrl || '',
    });
    const isLogin = !!o.isLoginSignup;
    const loginView = new LoginView({
      isModalUpsell: this.isModalUpsell,
      upsellTemplate: this.upsellTemplate,
      title: o.title ? o.title : T_('Login to Freelancer.com'),
      welcomeMsg: o.welcomeMsg ? o.welcomeMsg : '',
      keep: o.keep ? o.keep : false,
      vent: this.vent,
      model: loginModel,
      hideFacebook: !!o.hideFacebook,
      hideLookingFor: !!o.hideLookingFor,
      isLoginSignup: isLogin,
      isFreemarket: !!o.isFreemarket,
      switchToAlternateSignup:
        _.isObject(o) && o.hasOwnProperty('switchToAlternateSignup')
          ? o.switchToAlternateSignup
          : false,
    });

    this.switchView(loginView, isLogin);
  },

  /**
   * Show the recover password form
   */
  showRecoverPassword(options) {
    const isLogin = options ? options.isLoginSignup : false;
    const recoverPasswordModel = new RecoverPasswordModel();
    const recoverPasswordView = new RecoverPasswordView({
      model: recoverPasswordModel,
      vent: this.vent,
      loginTitle: options ? options.loginTitle : '',
      signupTitle: options ? options.signupTitle : '',
      hideFacebook: options ? options.hideFacebook : false,
      hideLookingFor: options ? options.hideLookingFor : false,
      isLoginSignup: isLogin,
      isFreemarket: options ? options.isFreemarket : false,
      switchToAlternateSignup:
        _.isObject(options) && options.hasOwnProperty('switchToAlternateSignup')
          ? options.switchToAlternateSignup
          : false,
    });

    this.switchView(recoverPasswordView, isLogin);
  },

  /**
   * Show the recover password form
   */
  showFBLogin(options) {
    let signupModel;

    if (options && options.signupModel) {
      signupModel = options.signupModel;
    } else {
      signupModel = new SignupModel();
    }
    const fbLoginModel = new FBLoginModel();
    const fbLoginView = new FBLoginView({
      model: fbLoginModel,
      signupModel,
      vent: this.vent,
    });
    this.switchView(fbLoginView, false);
    fbLoginModel.checkLoginStatus();
  },

  /**
   * Show the captcha challenge
   *
   * @param {object}
   * Accepted keys are:
   * - captcha {object} The captcha challenge to be resolved
   * - signupModel {model} The current signup session
   */
  showCaptcha(options) {
    if (options && options.captcha && options.signupModel) {
      const captchaModel = new CaptchaModel({
        captcha_breakpoint_id: options.captcha.captcha_breakpoint_id,
        captcha_breakpoint_token: options.captcha.captcha_breakpoint_token,
        public_key: options.captcha.public_key,
      });
      const captchaView = new CaptchaView({
        model: captchaModel,
        vent: this.vent,
        signupModel: options.signupModel,
      });
      this.switchView(captchaView, false);
    }
  },

  switchView(view, isLogin) {
    this.closeCurrentView();
    this.el.html(view.render().el);
    // Block the scrolling of the main container
    if (!isLogin) {
      jq('body').addClass('modal-open');
    }
    this.view = view;
  },

  closeCurrentView() {
    if (this.view) {
      if (this.view.destroy) {
        this.view.destroy();
      } else {
        this.view.remove();
      }
    }
    // Re-enable the scrolling of the main container
    jq('body').removeClass('modal-open');
  },

  setBackdrop() {
    if (this.backdrop && !this.isBackdropSet) {
      this.$backdrop = jq(
        '<div id="loginsignup-backdrop" class="modal-backdrop"></div>',
      );
      this.$backdrop.appendTo(document.body);
      this.isBackdropSet = true;
    }
  },

  removeBackdrop() {
    this.isBackdropSet = false;
    jq('#loginsignup-backdrop').remove();
  },

  onSignupSuccess(options) {
    if (options && options.isEmployer) {
      if (this.employerSignupClbk) {
        this.employerSignupClbk();
      } else {
        // buyer will always be redirected to post project page
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('signupSourcePage');
        }

        if (options.redirectUrl) {
          window.location = options.redirectUrl;
        } else {
          window.location = '/post-project?new_signup=true';
        }
      }
    } else if (this.workerSignupClbk) {
      this.workerSignupClbk();
    } else if (this.isLocalProject) {
      window.location.reload();
    } else if (options.redirectUrl) {
      window.location = options.redirectUrl;
    } else {
      window.location = '/sellers/skill-select.php';
    }
  },

  onLoginSuccess() {
    if (this.loginClbk) {
      this.loginClbk();
    } else {
      window.location.reload();
    }
  },

  onLoginRedirection(options) {
    if (options.suspended) {
      window.location = options.toURL;
    } else if (this.loginClbk) {
      this.loginClbk();
    } else {
      window.location = options.toURL;
    }
  },

  onClose(clickedFacebookButton) {
    if (typeof this.closeModalClbk === 'function') {
      this.closeModalClbk(clickedFacebookButton);
    } else {
      this.closeCurrentView();
      this.removeBackdrop();
    }
  },
});
